import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Alert, Button, Input, Label, Spinner } from "../../../Components/ui";
import { AlimTalkTemplate, notificationCenterService } from "../../../api/services/notification-center.service";
import { getAlimTalkTemplateName } from "./alimtalkTemplate";

export default function NotificationTemplateVisibility({ templates, visibleIds, onSaved, onSavingChange }: {
  templates: AlimTalkTemplate[]; visibleIds: number[]; onSaved: (ids: number[]) => void; onSavingChange: (saving: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const save = async () => {
    if (busy.current) return;
    busy.current = true; setSaving(true); setError(""); onSavingChange(true);
    try {
      const { data } = await notificationCenterService.saveComposerSetting(selection);
      if (!mounted.current) return;
      onSaved(data.visible_template_ids);
      setEditing(false);
      toast.success("통합 발송에 표시할 템플릿을 저장했습니다.");
    } catch (cause: any) {
      if (mounted.current) setError(cause?.response?.data?.detail || "템플릿 표시 설정을 저장하지 못했습니다. 다시 시도해 주세요.");
    } finally { busy.current = false; if (mounted.current) { setSaving(false); onSavingChange(false); } }
  };

  if (!editing) return <Button size="sm" color="soft-primary" className="mb-3" onClick={() => {
    setSelection(visibleIds); setSearch(""); setError(""); setEditing(true);
  }}>표시할 템플릿 선택 ({visibleIds.length}/{templates.length})</Button>;

  const rows = templates.filter((template) => `${getAlimTalkTemplateName(template)} ${template.template_code}`.toLowerCase().includes(search.trim().toLowerCase()));
  return <section aria-label="통합 발송 템플릿 표시 설정" className="border rounded p-3 mb-3">
    <h6>통합 발송에 표시할 템플릿</h6>
    <p className="text-muted small">이 지점에서 선택한 승인 템플릿만 표시합니다. 저장 후 새로 승인되는 템플릿도 여기서 선택해 주세요.</p>
    <fieldset disabled={saving}>
      <Label htmlFor="visible-template-search">템플릿 검색</Label>
      <Input id="visible-template-search" value={search} onChange={(event) => setSearch(event.target.value)} />
      <div className="d-flex flex-wrap gap-2 align-items-center my-2">
        <Button size="sm" color="light" onClick={() => setSelection(templates.map((template) => template.id))}>전체 표시</Button>
        <Button size="sm" color="light" onClick={() => setSelection([])}>전체 숨김</Button>
        <span className="small">{selection.length}개 선택</span>
      </div>
      <div className="max-h-64 overflow-y-auto border rounded p-2">
        {rows.map((template) => <Label key={template.id} className="d-flex align-items-start gap-2 p-2 mb-0">
          <Input type="checkbox" className="form-check-input shrink-0" checked={selection.includes(template.id)} onChange={(event) => setSelection((current) => event.target.checked ? [...current, template.id] : current.filter((id) => id !== template.id))} />
          <span className="min-w-0 break-words">{getAlimTalkTemplateName(template)} <small className="text-muted">({template.template_code || template.id})</small></span>
        </Label>)}
        {!rows.length ? <p className="text-muted mb-0">검색 결과가 없습니다.</p> : null}
      </div>
      {!selection.length ? <p className="text-muted small mt-2">저장하면 통합 발송의 템플릿 선택 목록이 비워집니다.</p> : null}
      {error ? <Alert color="danger" className="mt-2">{error}</Alert> : null}
      <div className="d-flex gap-2 mt-3">
        <Button color="primary" onClick={() => void save()}>{saving ? <><Spinner size="sm" /> 저장 중</> : "표시 설정 저장"}</Button>
        <Button color="light" onClick={() => setEditing(false)}>취소</Button>
      </div>
    </fieldset>
  </section>;
}
